/* iter-52a — Currency context.
 *
 * Lets any component read the user's chosen display currency + convert
 * canonical USD-cent amounts to it. Per-user preference lives in
 * localStorage (key: `prowriter.currency`). The admin's storefront
 * default is read from `/api/admin/config` (`default_currency`).
 *
 * Stripe charging stays USD-only — this context is purely about how
 * prices RENDER. The conversion is a snapshot taken at request time.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) throw new Error('REACT_APP_BACKEND_URL is required.');
const API = `${BACKEND_URL}/api`;
const LS_KEY = 'prowriter.currency';

const CurrencyContext = createContext({
  currency: 'USD',
  setCurrency: () => {},
  supported: [],
  rates: { USD: 1.0 },
  ratesFetchedAt: '',
  convertCents: (cents) => cents,
  format: (cents) => `$${(cents / 100).toFixed(2)}`,
});

/** Format an amount of minor units (e.g. cents) into a display string. */
function formatAmount(amount, code, supported) {
  const meta = supported.find(c => c.code === code) || { symbol: '$', decimals: 2 };
  const divisor = meta.decimals === 0 ? 1 : 100;
  const value   = amount / divisor;
  const fmt = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: meta.decimals,
    maximumFractionDigits: meta.decimals,
  }).format(value);
  return `${meta.symbol}${fmt}`;
}

export function CurrencyProvider({ children }) {
  const location = useLocation();
  const [supported, setSupported] = useState([]);
  const [rates, setRates]         = useState({ USD: 1.0 });
  const [ratesFetchedAt, setRatesFetchedAt] = useState('');
  const [adminDefault, setAdminDefault] = useState('USD');
  const [currency, setCurrencyState] = useState(() => {
    if (typeof window === 'undefined') return 'USD';
    return window.localStorage.getItem(LS_KEY) || 'USD';
  });

  // Load supported + rates once on mount. Admin default is read from a
  // cheap public-safe endpoint (the admin /api/admin/config requires
  // session; we fall through to USD if not available).
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/currency/supported`),
      axios.get(`${API}/currency/rates`),
    ]).then(([sup, rt]) => {
      if (cancelled) return;
      setSupported(sup.data?.currencies || []);
      setRates(rt.data?.rates || { USD: 1.0 });
      setRatesFetchedAt(rt.data?.fetched_at || '');
    }).catch(() => { /* best-effort — render in USD */ });
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Fetch the admin storefront default. Public read via the explicit
  // /api/currency endpoints would be cleaner; today /admin/config is
  // admin-gated so the unauth landing page just sticks with USD until
  // the user picks. Acceptable for v1.
  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return undefined;
    let cancelled = false;
    const loadAdminDefault = () => {
      if (typeof window === 'undefined' || !window.localStorage.getItem('pw-admin-user')) return;
      axios.get(`${API}/admin/config`).then(r => {
      if (cancelled) return;
      const c = (r.data?.default_currency || 'USD').toUpperCase();
      setAdminDefault(c);
      // If the user never explicitly picked one, adopt the admin's default.
      if (typeof window !== 'undefined' && !window.localStorage.getItem(LS_KEY)) {
        setCurrencyState(c);
      }
      }).catch(() => { /* expired admin hint — shell handles auth */ });
    };
    loadAdminDefault();
    window.addEventListener('admin:authenticated', loadAdminDefault);
    return () => { cancelled = true; window.removeEventListener('admin:authenticated', loadAdminDefault); };
  }, [location.pathname]);

  const setCurrency = useCallback((code) => {
    if (!code) return;
    const upper = String(code).toUpperCase();
    setCurrencyState(upper);
    try { window.localStorage.setItem(LS_KEY, upper); } catch { /* noop */ }
  }, []);

  const convertCents = useCallback((amountUsdCents) => {
    const meta = supported.find(c => c.code === currency) || { decimals: 2 };
    const rate = rates[currency] || 1.0;
    if (meta.decimals === 2) {
      return Math.round((amountUsdCents || 0) * rate);
    }
    // JPY: USD cents × rate / 100 → yen
    return Math.round(((amountUsdCents || 0) / 100) * rate);
  }, [currency, rates, supported]);

  /** iter-53f — Convert USD cents to an *arbitrary* target currency
   * (not necessarily the active picker selection). Required by the
   * Pricing Audit grid, which renders every supported currency in
   * the same row regardless of which one the admin is viewing the
   * storefront in. Always returns minor units in the target. */
  const convertTo = useCallback((amountUsdCents, code) => {
    const meta = supported.find(c => c.code === code) || { decimals: 2 };
    const rate = rates[code] || 1.0;
    if (meta.decimals === 2) {
      return Math.round((amountUsdCents || 0) * rate);
    }
    return Math.round(((amountUsdCents || 0) / 100) * rate);
  }, [rates, supported]);

  const format = useCallback((amountUsdCents) => {
    const converted = convertCents(amountUsdCents);
    return formatAmount(converted, currency, supported);
  }, [convertCents, currency, supported]);

  /** iter-53d — Format a book/product price honouring admin-pinned
   * per-currency overrides. If `overrides[currency]` is set, it is
   * already in that currency's minor units (e.g. EUR cents, whole JPY)
   * and we format it directly; otherwise we fall back to FX conversion
   * of `amountUsdCents`. */
  const formatPrice = useCallback((amountUsdCents, overrides) => {
    if (overrides && overrides[currency] != null) {
      return formatAmount(overrides[currency], currency, supported);
    }
    return format(amountUsdCents);
  }, [currency, supported, format]);

  const value = useMemo(() => ({
    currency, setCurrency,
    supported, rates, ratesFetchedAt,
    adminDefault,
    convertCents, convertTo, format, formatPrice,
  }), [currency, setCurrency, supported, rates, ratesFetchedAt, adminDefault, convertCents, convertTo, format, formatPrice]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

/** Convenience component — render an amount with the current currency.
 *
 * Pass `overrides={{EUR: 999, GBP: 799}}` to use an admin-pinned price
 * in the active currency; otherwise the `usdCents` value is FX-converted.
 */
export function Money({ usdCents, overrides, className, dataTestId }) {
  const { formatPrice } = useCurrency();
  return (
    <span className={className} data-testid={dataTestId}>{formatPrice(usdCents, overrides)}</span>
  );
}
